import { MonitoringZoneDto, AssetVideoProcessorDto } from '@/lib/api/types.gen';

export const calculateZoneDimensions = (cameras: unknown[], speakers: unknown[], relays: unknown[]) => {
  const deviceWidth = 180;
  const rowHeight = 90;
  const headerHeight = 40;
  const padding = 20;
  
  // Calculate cameras per row (split if more than 6)
  const camerasPerRow = cameras.length > 6 ? Math.ceil(cameras.length / 2) : cameras.length;
  
  // Calculate max devices per row considering camera splitting
  const maxDevicesPerRow = Math.max(camerasPerRow, speakers.length, relays.length);
  
  const width = Math.max(300, (maxDevicesPerRow * deviceWidth) + (padding * 2));
  const height = headerHeight + (padding * 2) + (rowHeight * 3) + 350; // Increased by 50px
  
  return { width, height };
};

export const calculateZonePositions = (zones: any[]) => {
  const baseX = 100;
  const baseY = 100;
  const gap = 30;
  
  if (zones.length === 1) {
    return [{ x: baseX, y: baseY }];
  } else if (zones.length === 2) {
    return [
      { x: baseX, y: baseY },
      { x: baseX, y: baseY + zones[0].dimensions.height + gap }
    ];
  } else {
    const positions: { x: number; y: number }[] = [];
    
    zones.forEach((_, index) => {
      const row = index % 2;
      
      if (index === 0) {
        positions.push({ x: baseX, y: baseY });
      } else if (index === 1) {
        positions.push({ x: baseX, y: baseY + zones[0].dimensions.height + gap });
      } else {
        const leftSiblingIndex = index - 2;
        const leftSiblingPos = positions[leftSiblingIndex];
        const leftSiblingZone = zones[leftSiblingIndex];
        
        const x = leftSiblingPos.x + leftSiblingZone.dimensions.width + gap;
        const y = row === 0 ? baseY : baseY + zones[row === 0 ? 0 : 1].dimensions.height + gap;
        
        positions.push({ x, y });
      }
    });
    
    return positions;
  }
};

export const groupDevicesByZone = (monitoredCameras: any[], allSpeakers: any[], allRelays: any[], site: any) => {
  const camerasByZone = new Map();
  const speakersByZone = new Map();
  const relaysByZone = new Map();
  const defaultCameras: any[] = [];
  
  // Group cameras by zones
  monitoredCameras.forEach((camera) => {
    const assignedZone = site?.monitoringZones?.find((zone: MonitoringZoneDto) => 
      zone.videoProcessors?.some((vp: AssetVideoProcessorDto) => vp.videoProcessorConfigId === camera.id)
    );
    
    
    if (assignedZone) {
      const zoneId = assignedZone.id;
      if (!camerasByZone.has(zoneId)) {
        camerasByZone.set(zoneId, []);
      }
      camerasByZone.get(zoneId).push(camera);
    } else {
      defaultCameras.push(camera);
    }
  });
  
  // Group speakers by zones based on related component groups
  allSpeakers.forEach((speaker) => {
    if (speaker.relatedComponentGroups && speaker.relatedComponentGroups.length > 0) {
      const associatedCameras = monitoredCameras.filter(camera => {
        const groupMatch = speaker.relatedComponentGroups?.includes(camera.originalDevice?.id) || false;
        return groupMatch;
      });
      
      if (associatedCameras.length > 0) {
        const relevantZones = new Set<string>();
        
        associatedCameras.forEach(camera => {
          const assignedZone = site?.monitoringZones?.find((zone: MonitoringZoneDto) => 
            zone.videoProcessors?.some((vp: AssetVideoProcessorDto) => vp.videoProcessorConfigId === camera.id)
          );
          const zoneId = assignedZone?.id || 'default';
          relevantZones.add(zoneId);
        });
        
        const primaryZone = Array.from(relevantZones)[0];
        const associatedCameraIds = associatedCameras.map(c => c.id);
        
        if (!speakersByZone.has(primaryZone)) {
          speakersByZone.set(primaryZone, []);
        }
        speakersByZone.get(primaryZone).push({ 
          ...speaker, 
          associatedCameraIds,
          associatedCameraId: associatedCameras[0].id
        });
      }
    }
  });
  
  // Group relays by zones based on related component groups
  allRelays.forEach((relay) => {
    if (relay.relatedComponentGroups && relay.relatedComponentGroups.length > 0) {
      const associatedCameras = monitoredCameras.filter(camera => {
        const groupMatch = relay.relatedComponentGroups?.includes(camera.originalDevice?.id) || false;
        return groupMatch;
      });
      
      if (associatedCameras.length > 0) {
        const relevantZones = new Set<string>();
        
        associatedCameras.forEach(camera => {
          const assignedZone = site?.monitoringZones?.find((zone: MonitoringZoneDto) => 
            zone.videoProcessors?.some((vp: AssetVideoProcessorDto) => vp.videoProcessorConfigId === camera.id)
          );
          const zoneId = assignedZone?.id || 'default';
          relevantZones.add(zoneId);
        });
        
        const primaryZone = Array.from(relevantZones)[0];
        const associatedCameraIds = associatedCameras.map(c => c.id);
        
        if (!relaysByZone.has(primaryZone)) {
          relaysByZone.set(primaryZone, []);
        }
        relaysByZone.get(primaryZone).push({ 
          ...relay, 
          associatedCameraIds,
          associatedCameraId: associatedCameras[0].id
        });
      }
    }
  });

  return {
    camerasByZone,
    speakersByZone,
    relaysByZone,
    defaultCameras,
  };
};